import { Injectable } from '@angular/core';
import { collection, doc, Firestore, getDoc, getDocs, limit, orderBy, query, Timestamp, where } from 'firebase/firestore';
import { environment } from '../../../environments/environment';
import { MediaCacheService } from '../media-cache.service';

@Injectable({
  providedIn: 'root'
})
export class TeamService {

  private firestore: Firestore = environment.firestore;

  constructor(private mediaCache: MediaCacheService) { }

  // obter o numero de membros de uma equipa
  public async getTeamMembersCount(teamId: string): Promise<number> {

    try {
      const usersCollection = collection(this.firestore, 'users');
      let q = query(usersCollection, where('teamId', '==', teamId));
      const snapshot = await getDocs(q);

      return snapshot.size;
    }
    catch (error) {
      console.error('Erro ao obter número de membros da team:', error);
      return 0;
    }
  }

  // obter o próximo evento de uma equipa
  public async getNextEvent(teamId: string): Promise<{ id: string; eventStartDate: Timestamp } | null> {
    try {
      const eventsCollection = collection(this.firestore, 'events');
      const todayDate = Timestamp.fromDate(new Date());

      const q = query(
        eventsCollection,
        where('teamId', '==', teamId),
        where('eventStartDate', '>', todayDate),
        orderBy('eventStartDate', 'asc'),
        limit(1)
      );

      const snapshot = await getDocs(q);

      if (snapshot.empty) {
        return null;
      }

      const nextEventDoc = snapshot.docs[0];
      const nextEventData = nextEventDoc.data();

      return {
        id: nextEventDoc.id,
        eventStartDate: nextEventData['eventStartDate'] as Timestamp
      };
    } catch (error) {
      console.error('Erro ao obter próximo evento:', error);
      return null;
    }
  }

  async getTeamImages(teamId: string, teamData?: { coverUrl?: string; badgeUrl?: string }) {
    const [cover, badge] = await Promise.all([
      this.mediaCache.getTeamCoverUrl(teamId, teamData?.coverUrl),
      this.mediaCache.getTeamBadgeUrl(teamId, teamData?.badgeUrl),
    ]);

    return { cover, badge };
  }

  // obter admins da equipa
  async getTeamAdmins(teamId: string): Promise<string[]> {
    try {
      const teamRef = doc(this.firestore, 'teams', teamId);
      const teamSnap = await getDoc(teamRef);

      if (!teamSnap.exists()) return [];

      return teamSnap.data()['admins'] ?? [];
    }
    catch (error) {
      console.error(error);
      return [];
    }
  }

  // função para obter os membros da equipa (exceto o utilizador autenticado)
  async getTeamParticipantsExceptMe(teamId: string, userId: string): Promise<string[]> {
    try {
      const usersRef = collection(this.firestore, 'users');

      const q = query(
        usersRef,
        where('teamId', '==', teamId)
      );

      const snapshot = await getDocs(q);

      if (snapshot.empty) return [];

      return snapshot.docs
        .map(doc => doc.id)
        .filter(userId => userId !== userId);

    } catch (error) {
      console.error('Erro ao obter membros da equipa:', error);
      return [];
    }
  }
}
